import React, { Component } from 'react';
import Lightbox from 'lightbox-react';
import 'lightbox-react/style.css'; // This only needs to be imported once in your app
import { ArrowRight, Images } from "lucide-react";
import {
  Row,
  Col,
  Container
} from "reactstrap";

function normalizeGalleryImage(item, index) {
  if (typeof item === "string") {
    return {
      src: item,
      alt: `Sport dog gallery photo ${index + 1}`
    };
  }

  return {
    ...item,
    alt: item.alt || `Sport dog gallery photo ${index + 1}`
  };
}

export default class DogGallery extends Component {
  constructor(props) {
    super(props);

    this.lightboxThumbnailRefs = [];
    this.lightboxThumbnailStripRef = React.createRef();

    this.state = {
      photoIndex: 0,
      isOpen: false,
    };
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      this.state.isOpen &&
      (prevState.photoIndex !== this.state.photoIndex || !prevState.isOpen)
    ) {
      this.scrollActiveLightboxThumbnailIntoView();
    }
  }

  openLightbox(index) {
    this.setState({ photoIndex: index });
    this.setState({ isOpen: true });
  }

  scrollActiveLightboxThumbnailIntoView() {
    const activeThumbnail = this.lightboxThumbnailRefs[this.state.photoIndex];
    const thumbnailStrip = this.lightboxThumbnailStripRef.current;

    if (!activeThumbnail || !thumbnailStrip) return;

    const activeCenter = activeThumbnail.offsetLeft + activeThumbnail.offsetWidth / 2;
    const nextScrollLeft = activeCenter - thumbnailStrip.clientWidth / 2;

    thumbnailStrip.scrollTo({
      left: Math.max(0, nextScrollLeft),
      behavior: "smooth"
    });
  }

  renderLightboxThumbnails(images, photoIndex) {
    return (
      <div
        className="dog-gallery-lightbox-thumbnails"
        aria-label="Gallery thumbnails"
        ref={this.lightboxThumbnailStripRef}
      >
        {images.map((image, index) => (
          <button
            aria-label={`View photo ${index + 1} of ${images.length}`}
            aria-current={index === photoIndex ? "true" : undefined}
            className={`dog-gallery-lightbox-thumbnail${index === photoIndex ? " active" : ""}`}
            key={image.src}
            ref={element => {
              this.lightboxThumbnailRefs[index] = element;
            }}
            type="button"
            onClick={() => this.setState({ photoIndex: index })}
          >
            <img alt={image.alt} src={image.src} />
          </button>
        ))}
      </div>
    );
  }

  render() {
    let { photoIndex, isOpen } = this.state;
    const images = (this.props.reverseImages ? [...this.props.images].reverse() : this.props.images)
      .map(normalizeGalleryImage);
    const lightboxImages = images.map(image => image.src);
    const isTileVariant = this.props.variant === "tiles";
    const primaryImage = images[0];
    const supportingImages = images.slice(1, 5);
    const thumbnailImages = images.slice(5);
    const lightboxImagePadding = typeof window !== "undefined" && window.innerWidth < 768 ? 76 : 58;

    return (
      <div className={isTileVariant ? "dog-gallery-tiles" : "dog-gallery-editorial"}>
        <Container>
          {isTileVariant ? (
            <Row>
              {images.map((item, index) =>
                <Col md="2" className="padding-0 gallery-image" key={item.src}>
                  <button
                    aria-label={`Open photo ${index + 1} of ${images.length}`}
                    className="dog-gallery-tile-button"
                    type="button"
                    onClick={() => this.openLightbox(index)}
                  >
                    <img alt={item.alt} src={item.src} />
                  </button>
                </Col>
              )}
            </Row>
          ) : (
            <>
              <div className="dog-gallery-preview-grid">
                {primaryImage && (
                  <button
                    aria-label={`Open photo 1 of ${images.length}`}
                    className="dog-gallery-preview-primary dog-gallery-preview-button"
                    type="button"
                    onClick={() => this.openLightbox(0)}
                  >
                    <img
                      alt={primaryImage.alt}
                      src={primaryImage.src}
                      style={primaryImage.objectPosition ? { objectPosition: primaryImage.objectPosition } : undefined}
                    />
                  </button>
                )}
                {supportingImages.length > 0 && (
                  <div className="dog-gallery-supporting-grid">
                    {supportingImages.map((item, supportIndex) => {
                      const imageIndex = supportIndex + 1;

                      return (
                        <button
                          aria-label={`Open photo ${imageIndex + 1} of ${images.length}`}
                          className="dog-gallery-preview-button"
                          key={item.src}
                          type="button"
                          onClick={() => this.openLightbox(imageIndex)}
                        >
                          <img
                            alt={item.alt}
                            src={item.src}
                            style={item.objectPosition ? { objectPosition: item.objectPosition } : undefined}
                          />
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
              {thumbnailImages.length > 0 && (
                <div className="dog-gallery-thumbnail-strip" aria-label="More gallery photos">
                  {thumbnailImages.map((item, thumbnailIndex) => {
                    const imageIndex = thumbnailIndex + 5;

                    return (
                      <button
                        aria-label={`Open photo ${imageIndex + 1} of ${images.length}`}
                        className="dog-gallery-thumbnail-button"
                        key={item.src}
                        type="button"
                        onClick={() => this.openLightbox(imageIndex)}
                      >
                        <img
                          alt={item.alt}
                          src={item.src}
                          style={item.objectPosition ? { objectPosition: item.objectPosition } : undefined}
                        />
                      </button>
                    );
                  })}
                </div>
              )}
              <div className="dog-gallery-view-all-wrap">
                <button
                  className="dog-gallery-view-all"
                  type="button"
                  onClick={() => this.openLightbox(0)}
                >
                  <Images aria-hidden="true" size={17} strokeWidth={2.2} />
                  View all {images.length} photos
                  <ArrowRight aria-hidden="true" size={17} strokeWidth={2.2} />
                </button>
              </div>
            </>
          )}
        </Container>

        {isOpen && (
          <Lightbox
            mainSrc={lightboxImages[photoIndex]}
            nextSrc={lightboxImages[(photoIndex + 1) % lightboxImages.length]}
            prevSrc={lightboxImages[(photoIndex + lightboxImages.length - 1) % lightboxImages.length]}
            mainSrcThumbnail={lightboxImages[photoIndex]}
            nextSrcThumbnail={lightboxImages[(photoIndex + 1) % lightboxImages.length]}
            prevSrcThumbnail={lightboxImages[(photoIndex + lightboxImages.length - 1) % lightboxImages.length]}
            imageTitle={<span className="dog-gallery-lightbox-count">{photoIndex + 1} / {lightboxImages.length}</span>}
            imageCaption={this.renderLightboxThumbnails(images, photoIndex)}
            imagePadding={lightboxImagePadding}
            wrapperClassName="dog-gallery-lightbox"
            onCloseRequest={() => this.setState({ isOpen: false })}
            onMovePrevRequest={() =>
              this.setState({
                photoIndex: (photoIndex + lightboxImages.length - 1) % lightboxImages.length,
              })
            }
            onMoveNextRequest={() =>
              this.setState({
                photoIndex: (photoIndex + 1) % lightboxImages.length,
              })
            }
          />
        )}
      </div>
    );
  }
}
